import React, { useState, useMemo } from "react"
import { Box, Grid } from "@material-ui/core"
import NCButton from "../../shared/NCButton"
import AddChangeDriverModal from "../driver/AddChangeDriverModal"
import AddChangeAidMonitorModal from "../driver/AddChangeAidMonitorModal"
import { useGetRideDetails } from "../../services/queries"
import { loggedInUser } from "../../libs/utils"
import { useEffect } from "react"
import Leg from "./Leg"
import NCText from "../../shared/NCText"
import NCLoader from "../../shared/NCLoader"

const RideDetails = ({ id }) => {
  const user = loggedInUser()
  const { data: userTripsDetails, isLoading } = useGetRideDetails(
    {
      request_id: id,
      current: true,
      // Trips are paginated (10/page) server-side; a ride can have more trips than
      // one page (e.g. 12 → 2 pages), and if a client's A-leg lands on page 2 its
      // "A Leg" button is wrongly disabled. Fetch them all in one page.
      page_size: 100
    },
    // Must match the roles RequestDetails renders this tab for (Super Admin, Admin,
    // Dispatcher); otherwise the query never runs and the tab shows "No upcoming rides".
    { enabled: user.user_type === 1 || user.user_type === 2 || user.user_type === 4 }
  )

  const rideDetails = userTripsDetails?.data?.data?.results || []
  const [AddChangeModal, showAddChangeModal] = useState(false)
  const [AddChangeAidMonitorModalState, showAddChangeAidMonitorModal] = useState(false)
  const [selectedLeg, setSelectedLeg] = useState(1)
  const [selectedClientIdx, setSelectedClientIdx] = useState(0)
  const [selectedDateIdx, setSelectedDateIdx] = useState(null)
  const [currentRideDetails, setCurrentRideDetails] = useState()

  // Group trips by service date → labeled "Today", "Tomorrow", or the date itself.
  // A date section only appears if trips exist for it ("if tomorrow trip exist?").
  const dateGroups = useMemo(() => {
    const toDateStr = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    const todayStr = toDateStr(new Date())
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowStr = toDateStr(tomorrow)

    const groups = {}
    rideDetails.forEach(trip => {
      // Group by service_date, NOT pickup_time. The API serializes pickup_time in
      // a fixed offset (e.g. -04:00), so the A leg (morning in the rider's local
      // timezone) can render as the previous calendar day while the B leg renders
      // as today — splitting one service date across "Today"/"Tomorrow". service_date
      // is the timezone-correct scheduled day, so both legs always group together.
      const dateStr = trip.service_date ? String(trip.service_date).slice(0, 10) : "Unknown"
      if (!groups[dateStr]) {
        let label = dateStr
        if (dateStr === todayStr) label = "Today"
        else if (dateStr === tomorrowStr) label = "Tomorrow"
        else if (dateStr !== "Unknown") {
          const d = new Date(`${dateStr}T00:00:00`)
          label = isNaN(d) ? dateStr : d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        }
        groups[dateStr] = { label, trips: [] }
      }
      groups[dateStr].trips.push(trip)
    })
    return Object.entries(groups)
      .sort(([a], [b]) => (a === "Unknown" ? 1 : b === "Unknown" ? -1 : a.localeCompare(b)))
      .map(([dateStr, g]) => ({ dateStr, label: g.label, trips: g.trips }))
  }, [rideDetails])

  // Trips for the currently selected date
  const selectedDateTrips = dateGroups[selectedDateIdx]?.trips || []

  // Group trips by client name (within the selected date)
  const clientGroups = useMemo(() => {
    const groups = {}
    selectedDateTrips.forEach(trip => {
      const key = trip.name || trip.client?.name || "Unknown"
      if (!groups[key]) groups[key] = []
      groups[key].push(trip)
    })
    return Object.entries(groups).map(([name, trips]) => ({ name, trips }))
  }, [selectedDateTrips])

  const handleDateChange = (idx) => {
    setSelectedDateIdx(idx)
    setSelectedClientIdx(0)
  }

  // Default the selected date to "Today" on first load, and keep it valid
  // if the data refreshes with fewer dates.
  useEffect(() => {
    if (!dateGroups.length) return
    if (selectedDateIdx === null || selectedDateIdx >= dateGroups.length) {
      const todayIdx = dateGroups.findIndex(g => g.label === "Today")
      setSelectedDateIdx(todayIdx === -1 ? 0 : todayIdx)
      setSelectedClientIdx(0)
    }
  }, [dateGroups, selectedDateIdx])

  const isALeg = (trip) => trip.request_data?.type === 1
  const isBLeg = (trip) => trip.request_data?.type === 2

  // Find the first non-ended, non-terminal trip to auto-select as "current"
  const findCurrentTrip = (trips) => {
    const ACTIVE_STATUSES = ["ACTIVE", "PICKED_UP", "ENROUTE", "ARRIVED", "SCHEDULED"]
    const active = trips.find(t => ACTIVE_STATUSES.includes(t.status))
    if (active) return active
    return trips[0]
  }

  // Get trips for the selected client, sorted nearest first
  const selectedClient = clientGroups[selectedClientIdx]
  const clientTrips = useMemo(() =>
    [...(selectedClient?.trips || [])].sort((a, b) => new Date(a.pickup_time) - new Date(b.pickup_time)),
    [selectedClient?.trips]
  )

  // When client changes, auto-select the current (non-ended) trip
  useEffect(() => {
    if (clientTrips.length) {
      const currentTrip = findCurrentTrip(clientTrips)
      setCurrentRideDetails(currentTrip)
      // Set leg based on trip type
      if (isALeg(currentTrip)) {
        setSelectedLeg(1)
      } else if (isBLeg(currentTrip)) {
        setSelectedLeg(2)
      }
    }
  }, [selectedClientIdx, clientTrips])

  const handleALegDetails = () => {
    setSelectedLeg(1)
    const aLeg = clientTrips.find(isALeg)
    if (aLeg) setCurrentRideDetails(aLeg)
  }
  const handleBLegDetails = () => {
    setSelectedLeg(2)
    const bLeg = clientTrips.find(isBLeg)
    if (bLeg) setCurrentRideDetails(bLeg)
  }

  return rideDetails.length ?
    (<Grid container style={{ marginBottom: "10px" }}>
      <Grid
        container
        style={{
          border: "1px solid #F3F4F6",
          borderRadius: "15px"
        }}
      >
        <Grid
          item
          xs={12}
          style={{ background: "#F3F4F6", borderRadius: "15px 15px 0px 0px" }}
        >
          {/* Date selector — group by date (Today / Tomorrow / etc.) */}
          {dateGroups.length > 1 && (
            <Box m={1} display={"flex"} flexWrap={"wrap"}>
              {dateGroups.map((g, idx) => (
                <NCButton
                  key={g.dateStr}
                  variant={selectedDateIdx === idx ? "contained" : "outlined"}
                  color={selectedDateIdx === idx ? "primary" : ""}
                  onClick={() => handleDateChange(idx)}
                  style={{ borderRadius: "5px", margin: "5px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {g.label}
                </NCButton>
              ))}
            </Box>
          )}

          {/* Client selector buttons */}
          {clientGroups.length > 1 && (
            <Box m={1} display={"flex"} flexWrap={"wrap"}>
              {clientGroups.map((group, idx) => (
                <NCButton
                  key={group.name}
                  variant={selectedClientIdx === idx ? "contained" : "outlined"}
                  color={selectedClientIdx === idx ? "primary" : ""}
                  onClick={() => setSelectedClientIdx(idx)}
                  style={{ borderRadius: "5px", margin: "5px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {group.name}
                </NCButton>
              ))}
            </Box>
          )}

          {/* A Leg / B Leg toggle */}
          <Box m={1} display={"flex"}>
            <NCButton
              variant={selectedLeg === 1 ? "contained" : "outlined"}
              color={selectedLeg === 1 ? "primary" : "default"}
              onClick={() => handleALegDetails()}
              style={{ borderRadius: "5px", width: "100px", margin: "10px" }}
              disabled={!clientTrips.find(isALeg)}
            >
              A Leg
            </NCButton>
            <NCButton
              variant={selectedLeg === 2 ? "contained" : "outlined"}
              color={selectedLeg === 2 ? "primary" : "default"}
              onClick={() => handleBLegDetails()}
              style={{ borderRadius: "5px", width: "100px", margin: "10px" }}
              disabled={!clientTrips.find(isBLeg)}
            >
              B Leg 
            </NCButton>
          </Box>
        </Grid>
        <Leg key={currentRideDetails?.id} currentRideDetails={currentRideDetails} showAddChangeModal={showAddChangeModal} showAddChangeAidMonitorModal={showAddChangeAidMonitorModal} />
        <AddChangeDriverModal
          modalState={AddChangeModal}
          tripId={currentRideDetails?.id}
          selectedDriver={currentRideDetails?.driver?.id}
          skipAvailability={true}
          handleClose={() => {
            showAddChangeModal(false)
          }}
        />
        <AddChangeAidMonitorModal
          modalState={AddChangeAidMonitorModalState}
          tripId={currentRideDetails?.id}
          selectedAidMonitor={currentRideDetails?.aid_monitor?.id}
          handleClose={() => {
            showAddChangeAidMonitorModal(false)
          }}
        />
      </Grid>
    </Grid>) : isLoading ? <NCLoader open={isLoading} /> : (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} width="100%">
        <NCText style={{ color: "#6B7280", fontSize: 15, textAlign: "center" }}>No upcoming rides are available</NCText>
      </Box>
    )


}

export default RideDetails
